import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { howItWorksApi } from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { usePageSeo } from '../hooks/usePageSeo';
import HowItWorksContentRenderer from '../components/how-it-works/HowItWorksContentRenderer';
import ArticleContentRenderer from '../components/articles/ArticleContentRenderer';
import RelatedTopics from '../components/how-it-works/RelatedTopics';
import { categorySlugOf } from '../lib/howItWorksHierarchy';
import { useFavoritedIds } from '../hooks/useFavoritedIds';

const HowItWorksTopicPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const favoritedIds = useFavoritedIds('how_it_works_topic', [topicId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    howItWorksApi.getTopic(topicId)
      .then((data) => {
        if (!cancelled) setTopic(data);
      })
      .catch((err) => {
        console.error('Error fetching how-it-works topic:', err);
        if (!cancelled) navigate('/how-it-works');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [topicId, navigate]);

  usePageSeo(
    topic ? `${topic.title} | AUTOXIUM` : undefined,
    topic ? (topic.description || topic.short_description) : undefined,
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 md:px-8 py-16 max-w-screen-2xl mx-auto space-y-4" data-testid="how-it-works-topic-loading">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-32 w-full mt-8" />
      </div>
    );
  }

  if (!topic) return null;

  const favorite = {
    contentType: 'how_it_works_topic',
    contentId: topicId,
    initialFavorited: favoritedIds.has(topicId),
  };

  // Back link goes to the topic's own domain "More Topics" listing (not straight to the
  // main landing page), so the hierarchy reads Topic -> Domain -> How Does It Work?. Only
  // topics with no category (shouldn't normally happen post-taxonomy-restructure) fall
  // back to the landing page directly.
  const backTo = topic.category
    ? `/how-it-works/section/${categorySlugOf(topic)}/more`
    : '/how-it-works';
  const backLabel = topic.category ? `Back to ${topic.category}` : 'Back to How Does It Work?';

  return (
    <div data-testid="how-it-works-topic-page">
      {topic.sections ? (
        <ArticleContentRenderer
          content={topic}
          onDownloadPdf={() => howItWorksApi.downloadTopicPdf(topicId)}
          favorite={favorite}
          backTo={backTo}
          backLabel={backLabel}
        />
      ) : (
        <HowItWorksContentRenderer topic={topic} favorite={favorite} />
      )}
      <RelatedTopics topicId={topicId} />
    </div>
  );
};

export default HowItWorksTopicPage;
